import React, { useCallback, useState } from 'react';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import WaAdminShell from '../../components/admin/whatsapp/WaAdminShell';
import WaKpiGrid from '../../components/admin/whatsapp/WaKpiGrid';
import WaFunnelChart from '../../components/admin/whatsapp/WaFunnelChart';
import WaSessionsTable from '../../components/admin/whatsapp/WaSessionsTable';
import WaSessionDrawer from '../../components/admin/whatsapp/WaSessionDrawer';
import WaBotHealthCard from '../../components/admin/whatsapp/WaBotHealthCard';
import WaConversionPanel from '../../components/admin/whatsapp/WaConversionPanel';
import {
  fetchWaMetricsToday,
  fetchWaActiveSessions,
  fetchWaFunnel,
  fetchWaAbandoned,
  fetchWaHealth,
  fetchWaConversion,
  forceHandoff,
  waLogsExportUrl,
} from '../../utils/adminWhatsapp';
import { stageLabel } from '../../components/admin/whatsapp/waStageLabels';

export default function WhatsAppDashboard() {
  const [metrics, setMetrics] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [funnel, setFunnel] = useState({});
  const [abandoned, setAbandoned] = useState([]);
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [drawerId, setDrawerId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [m, s, f, a, h] = await Promise.all([
        fetchWaMetricsToday(),
        fetchWaActiveSessions(),
        fetchWaFunnel(),
        fetchWaAbandoned(),
        fetchWaHealth(),
      ]);
      setMetrics(m);
      setSessions(s.sessions || []);
      setFunnel(f.funnel || {});
      setAbandoned(a.sessions || []);
      setHealth(h);
      setLastUpdated(format(new Date(), 'HH:mm:ss'));
    } catch {
      setMetrics(null);
      setSessions([]);
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [load]);

  const handleHandoff = async (id) => {
    if (!window.confirm('Force handoff to human agent?')) return;
    await forceHandoff(id);
    load();
    setDrawerId(null);
  };

  return (
    <WaAdminShell loading={loading} onRefresh={load} lastUpdated={lastUpdated}>
      <div className="mb-4 flex flex-wrap gap-2">
        <a
          href={waLogsExportUrl()}
          className="text-sm text-[#659A9B] hover:underline"
          target="_blank"
          rel="noreferrer"
        >
          Export logs CSV
        </a>
        <Link to="/admin/whatsapp/simulator" className="text-sm text-[#659A9B] hover:underline">
          Open simulator
        </Link>
      </div>

      <WaKpiGrid metrics={metrics} loading={loading} />

      <div className="grid lg:grid-cols-3 gap-4 mb-6">
        <div className="lg:col-span-2 space-y-4">
          <WaConversionPanel onLoad={(p) => fetchWaConversion(p)} />
          <WaFunnelChart funnel={funnel} title="Live pipeline (active stages)" />
        </div>
        <WaBotHealthCard health={health} loading={loading} />
      </div>

      {abandoned.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6">
          <h2 className="font-medium text-amber-900 mb-2">Recent abandonments ({abandoned.length})</h2>
          <ul className="text-sm space-y-1">
            {abandoned.slice(0, 8).map((row) => (
              <li key={row.id} className="flex flex-wrap gap-2 items-center text-amber-900">
                <span className="font-mono text-xs">{row.phone_masked}</span>
                <span>{stageLabel(row.stage)}</span>
                <Link to={`/admin/whatsapp/session/${row.id}`} className="text-xs underline">
                  Transcript
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
        <div className="px-4 py-3 border-b font-semibold text-[#1B375B]">Live sessions</div>
        <WaSessionsTable
          sessions={sessions}
          loading={loading}
          onSelect={setDrawerId}
          onHandoff={handleHandoff}
        />
      </div>

      {drawerId && (
        <WaSessionDrawer
          sessionId={drawerId}
          onClose={() => setDrawerId(null)}
          onHandoff={handleHandoff}
        />
      )}
    </WaAdminShell>
  );
}
